/**
 * Thin, typed wrapper around the native IndexedDB API for the Offline + Print
 * mode feature. No deps. Single database `homeschool-offline` v1.
 *
 * Stores:
 *  - activity-bundles   key: activityId (string)
 *      value: { activityId, cachedAt, manifest, assets: [{ url, blobKey }] }
 *  - bundle-blobs       key: content-hash (string) -> Blob
 *  - portfolio-artifacts key: artifactId (string)
 *      value: { artifactId, childId, activityId?, createdAt, kind, text?, blobKey? }
 *  - artifact-blobs     key: artifactId (string) -> Blob
 *  - wal-queue          auto-increment key
 *      value: { id, createdAt, method, url, bodyBlobKey?, headers, attempts, lastError? }
 *  - wal-blobs          key: blobKey (uuid string) -> Blob
 *
 * Migration note: portfolioStore (Zustand + localStorage) has NOT yet been
 * migrated to use `portfolio-artifacts`. When that migration is done, it
 * should read the localStorage persisted state, push each artifact + blob
 * into IDB, then clear the localStorage key. See src/stores/portfolioStore.ts.
 */

export const DB_NAME = 'homeschool-offline';
export const DB_VERSION = 1;

export const STORE_ACTIVITY_BUNDLES = 'activity-bundles';
export const STORE_BUNDLE_BLOBS = 'bundle-blobs';
export const STORE_PORTFOLIO_ARTIFACTS = 'portfolio-artifacts';
export const STORE_ARTIFACT_BLOBS = 'artifact-blobs';
export const STORE_WAL_QUEUE = 'wal-queue';
export const STORE_WAL_BLOBS = 'wal-blobs';

export type StoreName =
  | typeof STORE_ACTIVITY_BUNDLES
  | typeof STORE_BUNDLE_BLOBS
  | typeof STORE_PORTFOLIO_ARTIFACTS
  | typeof STORE_ARTIFACT_BLOBS
  | typeof STORE_WAL_QUEUE
  | typeof STORE_WAL_BLOBS;

export interface ActivityBundleAsset {
  url: string;
  blobKey: string;
}

export interface ActivityBundleRecord {
  activityId: string;
  cachedAt: number;
  manifest: unknown;
  assets: ActivityBundleAsset[];
}

export type ArtifactKind = 'photo' | 'note' | 'audio' | 'milestone';

export interface PortfolioArtifactRecord {
  artifactId: string;
  childId: string;
  activityId?: string;
  createdAt: number;
  kind: ArtifactKind;
  text?: string;
  blobKey?: string;
}

export interface WalQueueRow {
  id?: number;
  createdAt: number;
  method: string;
  url: string;
  bodyBlobKey?: string;
  headers: Record<string, string>;
  attempts: number;
  lastError?: string;
}

// Concurrent callers share one open promise.
let openPromise: Promise<IDBDatabase> | null = null;

function promisifyRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function openDb(): Promise<IDBDatabase> {
  if (openPromise) return openPromise;
  openPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable in this environment'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_ACTIVITY_BUNDLES)) {
        db.createObjectStore(STORE_ACTIVITY_BUNDLES, { keyPath: 'activityId' });
      }
      if (!db.objectStoreNames.contains(STORE_BUNDLE_BLOBS)) {
        db.createObjectStore(STORE_BUNDLE_BLOBS);
      }
      if (!db.objectStoreNames.contains(STORE_PORTFOLIO_ARTIFACTS)) {
        db.createObjectStore(STORE_PORTFOLIO_ARTIFACTS, { keyPath: 'artifactId' });
      }
      if (!db.objectStoreNames.contains(STORE_ARTIFACT_BLOBS)) {
        db.createObjectStore(STORE_ARTIFACT_BLOBS);
      }
      if (!db.objectStoreNames.contains(STORE_WAL_QUEUE)) {
        db.createObjectStore(STORE_WAL_QUEUE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_WAL_BLOBS)) {
        db.createObjectStore(STORE_WAL_BLOBS);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        // Another tab has a newer version; close so they can upgrade.
        db.close();
        openPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      openPromise = null;
      reject(req.error);
    };
    req.onblocked = () => {
      // Surface but don't reject; a later retry will succeed once other tabs close.
      console.warn('[idb] open blocked by another connection');
    };
  });
  return openPromise;
}

async function withStore<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDb();
  const tx = db.transaction(store, mode);
  const os = tx.objectStore(store);
  const result = await promisifyRequest(fn(os));
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error);
    tx.onerror = () => reject(tx.error);
  });
  return result;
}

export async function get<T = unknown>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return withStore<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
}

export async function put<T = unknown>(
  store: StoreName,
  value: T,
  key?: IDBValidKey
): Promise<IDBValidKey> {
  return withStore<IDBValidKey>(store, 'readwrite', (s) =>
    key === undefined ? s.put(value as unknown as object) : s.put(value as unknown as object, key)
  );
}

export async function del(store: StoreName, key: IDBValidKey): Promise<void> {
  await withStore<undefined>(store, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>);
}

export async function getAll<T = unknown>(store: StoreName): Promise<T[]> {
  return withStore<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
}

export async function getAllKeys(store: StoreName): Promise<IDBValidKey[]> {
  return withStore<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys());
}

export async function count(store: StoreName): Promise<number> {
  return withStore<number>(store, 'readonly', (s) => s.count());
}

export async function clear(store: StoreName): Promise<void> {
  await withStore<undefined>(store, 'readwrite', (s) => s.clear() as IDBRequest<undefined>);
}
